import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, documentId, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { currentUser, userRole, managerAssignments } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  // Phase 5: org membership comes from org_managers (loaded into
  // managerAssignments by AuthContext) — no longer from users.organizationIds.
  // Stable dependency: dedupe + sort the org id list so a re-render that
  // returns the same logical set doesn't re-trigger loadOrganizations.
  const userOrgIds = useMemo(() => {
    const ids = Array.from(new Set((managerAssignments || []).map(m => m.orgId).filter(Boolean)));
    ids.sort();
    return ids;
  }, [managerAssignments]);

  useEffect(() => {
    if (currentUser) {
      loadOrganizations();
    } else {
      setOrganizations([]);
      setCurrentOrgId(null);
      setLoading(false);
    }
  }, [currentUser, userRole, userOrgIds]);

  /**
   * Fetch orgs the user belongs to using a targeted query.
   * For org_owner/salesperson: uses where(documentId(), 'in', orgIds) instead of
   * fetching ALL orgs then filtering client-side.
   */
  const loadOrganizations = async () => {
    try {
      setLoading(true);

      if (userRole === 'superadmin') {
        // Superadmins can see all organizations. Generous safety cap per the
        // no-unbounded-queries rule — far above any realistic org count.
        const orgsSnapshot = await getDocs(query(collection(db, 'organizations'), limit(500)));
        const orgsData = orgsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setOrganizations(orgsData);
        setCurrentOrgId(null);
      } else {
        // Anyone else is scoped to the orgs they hold an org_managers row for.
        // Keyed off membership rather than users.role so plain managers (who
        // stay role='user') get scoped too — without a currentOrgId the
        // dashboard falls back to an unscoped, all-orgs query.
        if (userOrgIds.length > 0) {
          // Query only the orgs the user belongs to (Firestore 'in' supports up to 30)
          // For users with 30+ orgs, chunk into multiple queries
          const orgsData = [];
          for (let i = 0; i < userOrgIds.length; i += 30) {
            const chunk = userOrgIds.slice(i, i + 30);
            const orgsSnapshot = await getDocs(
              query(collection(db, 'organizations'), where(documentId(), 'in', chunk))
            );
            orgsSnapshot.docs.forEach(doc => {
              orgsData.push({ id: doc.id, ...doc.data() });
            });
          }

          setOrganizations(orgsData);

          // Set initial org (stored preference or first one)
          const storedOrgId = localStorage.getItem('selectedOrgId');
          if (storedOrgId && userOrgIds.includes(storedOrgId)) {
            setCurrentOrgId(storedOrgId);
          } else {
            setCurrentOrgId(userOrgIds[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = (orgId) => {
    // Superadmins can switch to any org (including null for "all")
    // Org owners can switch between their assigned orgs
    if (userRole === 'superadmin' || organizations.some(org => org.id === orgId)) {
      setSwitching(true);

      // Save preference for org-scoped users
      if (userRole !== 'superadmin' && orgId) {
        localStorage.setItem('selectedOrgId', orgId);
      }
      
      // Small delay to show loading state
      setTimeout(() => {
        setCurrentOrgId(orgId);
        setSwitching(false);
      }, 300);
    }
  };

  const isViewingAllOrgs = currentOrgId === null && userRole === 'superadmin';
  
  // When viewing a specific org (even as superadmin), use org owner permissions
  const effectiveRole = currentOrgId !== null ? 'org_owner' : userRole;
  
  const currentOrganization = currentOrgId 
    ? organizations.find(org => org.id === currentOrgId)
    : null;

  const canSwitchOrg = userRole === 'superadmin' || organizations.length > 1;
  
  const value = {
    currentOrgId,
    currentOrganization,
    organizations,
    isViewingAllOrgs,
    effectiveRole, // Role to use for UI permissions
    switchOrganization,
    switching,
    loading,
    canSwitchOrg
  };

  return (
    <OrganizationContext.Provider value={value}>
      {!loading && children}
    </OrganizationContext.Provider>
  );
};
